import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Location from "../../assets/images/table/Location";
import Store from "../../assets/images/table/Store";
import Button from "../../components/common/button/Button";
import DataTable, {
  Column,
} from "../../components/common/data-table/DataTable";
import StatisticCard from "../../components/common/statistic-card/StatisticCard";
import TitleUpdater from "../../components/common/title/TitleUpdater";
import styles from "./AllShipping.module.scss";
import { useAuth } from "../../context/AuthContext";
import { useCurrencies } from "../../context/CurrenciesContext";
import PreperedIcon from "../../assets/images/statistic-cards/PreperedIcon";
import OnTheWayIcon from "../../assets/images/statistic-cards/OnTheWayIcon";
import DeliveredIcon from "../../assets/images/statistic-cards/DeliveredIcon";
import OnStoresIcon from "../../assets/images/statistic-cards/OnStoresIcon";
import OrderService from "../../api/services/orders.service";
import Loading from "../../components/common/loading/Loading";
import { formatDate } from "../../utils/date";
import { toast } from "sonner";
import User from "../../assets/images/table/User";
import DateIcon from "../../assets/images/table/DateIcon";
import Status from "../../assets/images/table/Status";
import InfoIcon from "../../assets/images/table/InfoIcon";

type Order = {
  id: number;
  product_variant: any;
  price: number;
  quantity: number;
  store: any;
  created: string;
  status: string;
};

type TabType = "store" | "4" | "5" | "6";

const AllShipping = () => {
  const { t } = useTranslation("translation");
  const [activeTab, setActiveTab] = useState<TabType>("store");
  const [data, setData] = useState<Order[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusCounts, setStatusCounts] = useState({
    "1": 0,
    "2": 0,
    "3": 0,
    "4": 0,
    "5": 0,
    "6": 0,
  });
  const [actionLoading, setActionLoading] = useState<number | null>(null);

  const [pagination, setPagination] = useState({
    page: 1,
    count: 10,
    total: 0,
  });

  const { setButtons } = useAuth();
  const { currencyId, currency } = useCurrencies();

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await OrderService.getAllOrders(
        pagination.page,
        pagination.count,
        activeTab,
        currencyId,
      );

      const orders = response.data?.data || [];
      const total = response.data?.total || orders.length;
      const resultCounts = response.data?.result_counts || [];

      const counts = { "1": 0, "2": 0, "3": 0, "4": 0, "5": 0, "6": 0 };
      resultCounts.forEach((item: any) => {
        if (item.status in counts) {
          counts[item.status as keyof typeof counts] = item.count;
        }
      });
      setStatusCounts(counts);

      setData(orders);
      setPagination((prev) => ({ ...prev, total }));
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        (t("common.error_unexpected") as string);
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.count, activeTab, currencyId, t]);

  const changeOrderStatus = async (id: number, currentStatus: string) => {
    setActionLoading(id);
    try {
      await OrderService.chageStatus(id);
      const successMessages = {
        "4": t("all_shipping.messages.sent_for_delivery") as string,
        "5": t("all_shipping.messages.marked_delivered") as string,
        "6": t("all_shipping.messages.completed") as string,
      };

      toast.success(
        successMessages[currentStatus as keyof typeof successMessages] ||
          (t("all_shipping.messages.status_updated") as string),
      );
      fetchOrders();
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        (t("common.error_unexpected") as string);
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setActionLoading(null);
    }
  };

  const handleSendForDelivery = (id: number) => {
    changeOrderStatus(id, "4");
  };

  const handleMarkDelivered = (id: number) => {
    changeOrderStatus(id, "5");
  };

  const StatusButton = ({ status, id }: { status: string; id: number }) => {
    const isLoading = actionLoading === id;

    // Store-side orders (1, 2, 3) - admin can only view, not update
    if (status === "1" || status === "2" || status === "3") {
      return <span className={styles.statusText}>{getStatusText(status)}</span>;
    }

    switch (status) {
      case "4":
        return (
          <button
            className={`${styles.status} ${styles.deliveryBtn}`}
            onClick={() => handleSendForDelivery(id)}
            disabled={isLoading}
          >
            {isLoading
              ? (t("all_shipping.actions.processing") as string)
              : (t("all_shipping.actions.send_for_pickup") as string)}
          </button>
        );
      case "5":
        return (
          <button
            className={`${styles.status} ${styles.deliveredBtn}`}
            onClick={() => handleMarkDelivered(id)}
            disabled={isLoading}
          >
            {isLoading
              ? (t("all_shipping.actions.processing") as string)
              : (t("all_shipping.actions.mark_delivered") as string)}
          </button>
        );
      case "6":
        return (
          <button className={`${styles.status} ${styles.completeBtn}`}>
            {t("all_shipping.status.delivered") as string}
          </button>
        );
      default:
        return (
          <span className={styles.statusText}>{getStatusText(status)}</span>
        );
    }
  };

  const getStatusText = (status: string) => {
    const statusMap: { [key: string]: string } = {
      "1": t("all_shipping.status.incoming") as string,
      "2": t("all_shipping.status.packing") as string,
      "3": t("all_shipping.status.ready_for_admin") as string,
      "4": t("all_shipping.status.out_for_delivery") as string,
      "5": t("all_shipping.status.pickup_tasks") as string,
      "6": t("all_shipping.status.delivered") as string,
    };
    return statusMap[status] || status;
  };

  const handlePageChange = (newPage: number) => {
    if (
      newPage >= 1 &&
      newPage <= Math.ceil(pagination.total / pagination.count)
    ) {
      setPagination((prev) => ({ ...prev, page: newPage }));
    }
  };

  const handleLimitChange = (newLimit: number) => {
    setPagination((prev) => ({ ...prev, count: newLimit, page: 1 }));
  };

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
  };

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  useEffect(() => {
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, [activeTab]);

  useEffect(() => {
    setButtons([]);
    return () => setButtons([]);
  }, [setButtons]);

  const columns: Column[] = [
    {
      key: "name",
      header: <Store />,
      render: (_, row) => (
        <div className={styles.user_cell}>
          {row?.store?.logo_url ? (
            <img
              src={row?.store?.logo_url}
              alt={row?.store?.name}
              className={styles.user_avatar}
            />
          ) : (
            <div className={styles.no_image}>
              {row?.store?.name[0]}
              {row?.store?.name[1]}
            </div>
          )}
          <div className={styles.user_info}>
            <p className={styles.user_name}>{row?.store?.name}</p>
            <a href={`tel:${row?.user?.phone}`} className={styles.user_phone}>
              {row?.store?.contact_phone || ""}
            </a>
            <a
              href={`mailto:${row?.store?.email}`}
              className={styles.user_email}
            >
              {row?.store?.email}
            </a>
          </div>
        </div>
      ),
    },
    {
      key: "user",
      header: <User />,
      render: (_, row) => (
        <div className={styles.user_cell}>
          {row.user.profile_photo ? (
            <img
              src={row?.user?.profile_photo}
              alt={row?.user?.first_name}
              className={styles.user_avatar}
            />
          ) : (
            <div className={styles.no_image}>
              {row?.user?.first_name[0]}
              {row?.user?.last_name[0]}
            </div>
          )}
          <div className={styles.user_info}>
            <p className={styles.user_name}>
              {row?.user?.first_name} {row?.user?.last_name}
            </p>
            <a href={`tel:${row?.user?.phone}`} className={styles.user_phone}>
              {row?.user?.phone || ""}
            </a>
            <a
              href={`mailto:${row?.user?.email}`}
              className={styles.user_email}
            >
              {row?.user?.email}
            </a>
          </div>
        </div>
      ),
    },
    {
      key: "location",
      header: <Location />,
      render: (_, row) => (
        <div className={styles.box}>{row?.store?.business_address || "-"}</div>
      ),
    },
    {
      key: "price",
      header: <InfoIcon />,
      render: (_, row) => (
        <div className={styles.box}>
          <p>{row?.product_variant?.name_en || "-"}</p>
          <p>
            {row?.price?.toFixed(2) || "0.00"}{" "}
            {row?.currency_name || currency || ""}
          </p>
          <p>
            {row?.quantity || "-"}{" "}
            {t("all_shipping.table.product_count_label") as string}
          </p>
        </div>
      ),
    },
    {
      key: "created_at",
      header: <DateIcon />,
      render: (_, row) => formatDate(row?.created),
    },
    {
      key: "status",
      header: <Status />,
      render: (value, row) => {
        return <StatusButton status={value} id={row.id} />;
      },
    },
  ];

  const tabLabels = {
    store: t("all_shipping.stats.on_stores") as string,
    "4": t("all_shipping.stats.out_for_delivery") as string,
    "5": t("all_shipping.stats.pickup_tasks") as string,
    "6": t("all_shipping.stats.delivered") as string,
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <div className={styles.all_shipping}>
      <TitleUpdater title={t("all_shipping.title") as string} />
      <div className={styles.statistic_cards}>
        <StatisticCard
          icon={<OnStoresIcon />}
          title={t("all_shipping.stats.on_stores") as string}
          subtitle={(
            statusCounts["1"] +
            statusCounts["2"] +
            statusCounts["3"]
          ).toString()}
          onClick={() => handleTabChange("store")}
          active={activeTab === "store"}
        />
        <StatisticCard
          icon={<PreperedIcon />}
          title={t("all_shipping.stats.out_for_delivery") as string}
          subtitle={statusCounts["4"].toString()}
          onClick={() => handleTabChange("4")}
          active={activeTab === "4"}
        />
        <StatisticCard
          icon={<OnTheWayIcon />}
          title={t("all_shipping.stats.pickup_tasks") as string}
          subtitle={statusCounts["5"].toString()}
          onClick={() => handleTabChange("5")}
          active={activeTab === "5"}
        />
        <StatisticCard
          icon={<DeliveredIcon />}
          title={t("all_shipping.stats.delivered") as string}
          subtitle={statusCounts["6"].toString()}
          onClick={() => handleTabChange("6")}
          active={activeTab === "6"}
        />
      </div>

      {error && (
        <div className={styles.error}>
          {error}
          <button onClick={fetchOrders} className={styles.retryBtn}>
            {t("all_shipping.actions.try_again") as string}
          </button>
        </div>
      )}

      <div className={styles.tabContent}>
        {!loading && !error && (
          <DataTable
            table_title={`${tabLabels[activeTab]} ${t("all_shipping.table.orders") as string}`}
            data={data}
            columns={columns}
            paginationData={{
              total: pagination.total,
              page: pagination.page,
              limit: pagination.count,
              onPageChange: handlePageChange,
              onLimitChange: handleLimitChange,
            }}
            noDataMessage={t("all_shipping.table.no_orders") as string}
            other
          />
        )}
      </div>
    </div>
  );
};

export default AllShipping;
